/**
 * Рулетка открытия кейса.
 *
 * Лента строится заранее и выигрышный предмет ставится на фиксированное место:
 * анимация лишь доезжает до уже известного индекса. Поэтому картинка не может
 * разойтись с результатом сервера, за который уже списаны и начислены лапки.
 *
 * Остановка делается одним переходом с сильным замедлением в конце, а не
 * пружиной: пружина проносит ленту мимо приза и возвращает назад, из-за чего
 * кажется, будто результат «докрутили».
 */

import { useEffect, useRef, useState } from "react";

import type { CaseItemDto } from "@koshkodrop/shared";

import { RARITY_COLOR, rarityTileStyle } from "./rarity";

interface Props {
  items: CaseItemDto[];
  /** null до первого открытия: лента стоит и просто показывает содержимое. */
  winnerId: string | null;
  /** Меняется на каждом открытии: по нему лента перезапускается. */
  spinKey: number;
  running: boolean;
  onDone: () => void;
}

const TILE = 76;
const GAP = 8;
const STEP = TILE + GAP;
/** Позиция приза в ленте: до него должно быть достаточно кадров для разгона. */
const LAND_INDEX = 32;
const STRIP_LENGTH = 40;
const SPIN_MS = 3400;

export function CaseReel({ items, winnerId, spinKey, running, onDone }: Props) {
  // Позиция и признак анимации живут в одном состоянии: сброс ленты в начало
  // обязан произойти без перехода, иначе при повторном открытии она сначала
  // едет назад к нулю и только потом разгоняется
  const [motion, setMotion] = useState<{ offset: number; animate: boolean }>({
    offset: 0,
    animate: false,
  });
  const [landed, setLanded] = useState(false);
  const stripRef = useRef<CaseItemDto[]>([]);

  if (winnerId !== null && (stripRef.current.length === 0 || running)) {
    stripRef.current = buildStrip(items, winnerId, stripRef.current);
  }
  const strip = winnerId === null ? idleStrip(items) : stripRef.current;

  useEffect(() => {
    if (winnerId === null) {
      // Лента в покое: центрируем середину набора, чтобы по краям было видно,
      // что она продолжается, и кейс читался как рулетка, а не как список
      setMotion({ offset: Math.floor(idleStrip(items).length / 2) * STEP, animate: false });
      setLanded(false);
    }
  }, [winnerId, items]);

  useEffect(() => {
    if (!running) return;

    setLanded(false);
    setMotion({ offset: 0, animate: false });

    // Небольшой сдвиг от центра: идеально ровная остановка выглядит
    // механической, а лёгкое смещение — как настоящая рулетка
    const jitter = (Math.random() - 0.5) * (TILE * 0.5);
    const target = LAND_INDEX * STEP + jitter;

    // Второй кадр нужен, чтобы браузер успел отрисовать ленту в нуле без
    // перехода: из одного кадра он склеит сброс и разгон в один прыжок
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setMotion({ offset: target, animate: true }));
    });

    const timer = window.setTimeout(() => {
      setLanded(true);
      onDone();
    }, SPIN_MS + 60);

    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
      window.clearTimeout(timer);
    };
  }, [running, spinKey, onDone]);

  const winner = winnerId === null ? undefined : items.find((item) => item.id === winnerId);
  const accent = winner && landed ? RARITY_COLOR[winner.rarity] : "#ffc65c";

  return (
    <div className="relative overflow-hidden rounded-[26px] border border-white/10 bg-night-900">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center">
        <span
          className="size-0 border-x-[7px] border-t-[9px] border-x-transparent"
          style={{ borderTopColor: accent }}
        />
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-center">
        <span
          className="size-0 border-x-[7px] border-b-[9px] border-x-transparent"
          style={{ borderBottomColor: accent }}
        />
      </div>

      <div
        className="pointer-events-none absolute inset-y-3 left-1/2 z-10 -translate-x-1/2 rounded-2xl border-2"
        style={{ width: TILE + 6, borderColor: accent }}
      />

      {landed && (
        <div
          className="animate-reel-flash pointer-events-none absolute inset-0 z-10"
          style={{ background: `radial-gradient(circle at 50% 50%, ${accent}55, transparent 62%)` }}
        />
      )}

      <div className="reel-mask py-4">
        <div
          className="flex will-change-transform"
          style={{
            gap: GAP,
            transform: `translateX(calc(50% - ${TILE / 2}px - ${motion.offset}px))`,
            transition: motion.animate
              ? `transform ${SPIN_MS}ms cubic-bezier(0.08, 0.82, 0.12, 1)`
              : "none",
          }}
        >
          {strip.map((item, index) => (
            <ReelTile
              key={`${spinKey}-${index}`}
              item={item}
              highlight={landed && index === LAND_INDEX}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ReelTile({ item, highlight }: { item: CaseItemDto; highlight: boolean }) {
  return (
    <div
      className={`flex shrink-0 flex-col items-center justify-center rounded-2xl border transition-transform ${
        highlight ? "scale-[1.06]" : ""
      }`}
      style={{ width: TILE, height: TILE, ...rarityTileStyle(item.rarity, highlight) }}
    >
      <span className="text-[30px] leading-none">{item.emoji}</span>
      <span
        className="mt-1.5 max-w-[68px] truncate text-[9px] font-bold"
        style={{ color: RARITY_COLOR[item.rarity] }}
      >
        {item.name}
      </span>
    </div>
  );
}

/** Лента для состояния покоя: содержимое кейса, повторённое для краёв. */
function idleStrip(items: CaseItemDto[]): CaseItemDto[] {
  if (items.length === 0) return [];
  const out: CaseItemDto[] = [];
  for (let i = 0; i < 3; i += 1) out.push(...items);
  return out;
}

/**
 * Собирает ленту для прокрутки. Предметы до приза берутся случайно: лента —
 * это только оформление, результат уже посчитан сервером и зафиксирован
 * в позиции LAND_INDEX.
 */
function buildStrip(
  items: CaseItemDto[],
  winnerId: string,
  previous: CaseItemDto[],
): CaseItemDto[] {
  const winner = items.find((item) => item.id === winnerId) ?? items[0];
  if (!winner || items.length === 0) return [];

  const strip: CaseItemDto[] = [];
  for (let i = 0; i < STRIP_LENGTH; i += 1) {
    if (i === LAND_INDEX) {
      strip.push(winner);
      continue;
    }
    // Первые плитки оставляем от прошлой ленты, чтобы старт не мигал заменой
    const carried = previous[i];
    strip.push(i < 3 && carried ? carried : items[Math.floor(Math.random() * items.length)]!);
  }

  return strip;
}
